import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { MemberRoleMenuButton } from "@components/table/members/member-role-menu-button";
import { RemoveTeamMenuButton } from "@components/table/teams/remove-team-menu-button";
import { SdbTeam } from "@custom-types/teams-types";
import { DeleteIcon, SelectionIcon, UserRoleIcon, VisibleIcon } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useTeamUtils } from "@hooks/use-team-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { toggleItemSelection } from "@utils/array-utils";

/**
 * Custom hook to get the menu options for a team.
 * Provides actions for selecting, viewing details, changing role, and removing a team.
 */
export function useTeamMenuOptions(): MenuItemOption<SdbTeam>[] {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const { openTeamDetails } = useTeamUtils();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { canEditMember, canDeleteTeams } = useHasUserValidRoleCompanyLevel();

  return [
    {
      label: "Select team",
      Icon: SelectionIcon,
      onSelect: (team) => {
        const newSelectedRowIds = toggleItemSelection(selectedRowIds, team.id);
        dispatch(setInitialFetchingItems(newSelectedRowIds));
      },
      shouldShow: () => isScreenSmOrSmaller,
      shouldShowDividerAfter: isScreenSmOrSmaller,
    },
    {
      label: "View details",
      Icon: VisibleIcon,
      onSelect: (team) => {
        openTeamDetails({
          teamId: team.id,
          numberOfMembers: team.memberCount,
        });
      },
      shouldShowDividerAfter: true,
    },
    {
      label: "Change role",
      Icon: UserRoleIcon,
      component: (team, onMenuClose) => (
        <MemberRoleMenuButton
          member={team}
          companyId={companyId}
          subjectType="team"
          onMenuClose={onMenuClose}
        />
      ),
      shouldShow: () => canEditMember,
      shouldShowDividerAfter: true,
    },
    {
      label: "Remove team",
      Icon: DeleteIcon,
      component: (team, onMenuClose) => (
        <RemoveTeamMenuButton team={team} onMenuClose={onMenuClose} />
      ),
      shouldShow: (team) => canDeleteTeams && !team.category.isManaged,
    },
  ];
}
